import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AgentDetail, AgentField, AgentUpdate } from '@inbound/contract';
import { AGENT_INSTRUCTIONS_MAX, AGENT_NAME_MAX } from '@inbound/contract';

import { useGetAgentQuery, useUpdateAgentMutation } from '../../store/agents-api.js';
import { messageOf } from '../../store/error-message.js';
import { useAppDispatch } from '../../store/hooks.js';
import { Spinner } from '../../ui/BrandLogo.js';
import { pushToast } from '../../ui/toast-store.js';
import { IconChevronLeft } from '../../ui/icons.js';
import { when } from '../../ui/when.js';
import { AgentHistory } from './AgentHistory.js';
import { conflictOf } from './conflict.js';
import { Field } from './Field.js';
import { LineGreetingPanel } from './LineGreetingPanel.js';
import styles from './AgentEditor.module.css';

/** The editable agent fields, as the form holds them. */
interface Draft {
  name: string;
  instructions: string;
  dateContext: boolean;
}

/**
 * A draft, tagged with the agent it belongs to.
 *
 * The tag is the fix for a real bug. The draft used to be seeded by one effect
 * and cleared by another, keyed on different things; both ran on mount, the clear
 * ran second, and because a re-opened agent arrives straight from the RTK Query
 * cache the record never changed again — so the seeding effect never fired a
 * second time and the page said "Loading agent…" until a reload. One piece of
 * state carrying its own identity cannot get into that argument with itself.
 */
interface DraftState {
  agentId: string;
  values: Draft;
}

function draftOf(agent: AgentDetail): Draft {
  return {
    name: agent.name,
    instructions: agent.instructions,
    dateContext: agent.dateContext,
  };
}

const FIELDS = ['name', 'instructions', 'dateContext'] as const;

/** "a", "a and b" — a list an operator reads rather than one a machine emits. */
function readAsList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`;
}

/**
 * One agent's configuration (S10).
 *
 * ## What this offers, and why so little
 *
 * On the agent that answers: its spoken name, its instructions, whether it is
 * told today's date, and the greeting of each line it answers. On the analyzer:
 * instructions only. `editableFields` arrives with the record rather than being
 * inferred here, so the form cannot offer what the API would refuse.
 *
 * Greetings are saved on their own, per line: they live on the telephony preset,
 * not on the agent, and have their own version to conflict on.
 *
 * Everything else the platform holds — thirty configuration keys, tools, MCP
 * servers, knowledge bases — is neither shown nor touched. A save is a full
 * overwrite on the platform's side, and the console's job is to make that safe,
 * not to put it on screen.
 */
export function AgentEditor(): JSX.Element {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: agent, isLoading, isError, error } = useGetAgentQuery(agentId);
  const [saveAgent, agentSaving] = useUpdateAgentMutation();

  const [draft, setDraft] = useState<DraftState | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  /**
   * Seed the agent draft from the record, once per agent.
   *
   * The condition is which agent the draft belongs to, not whether the record
   * changed — a cached record does not change, and that is exactly the case that
   * used to leave this page stuck.
   */
  useEffect(() => {
    if (agent === undefined) return;

    setDraft((current) =>
      current?.agentId === agentId ? current : { agentId, values: draftOf(agent) },
    );
  }, [agent, agentId]);

  // Never render one agent's text against another's record: the effect above runs
  // after this render, so on the first render after a switch the draft still
  // describes the agent we just left.
  const values = draft?.agentId === agentId ? draft.values : null;

  const editable = useMemo(
    () => new Set<AgentField>(agent?.editableFields ?? []),
    [agent?.editableFields],
  );

  const changed = useMemo((): AgentField[] => {
    if (agent === undefined || values === null) return [];
    const original = draftOf(agent);

    return FIELDS.filter((field) => editable.has(field) && values[field] !== original[field]);
  }, [agent, values, editable]);

  const lineCount = useMemo(() => (values?.instructions ?? '').split('\n').length, [values]);

  const back = (): void => void navigate('/agents');

  if (isError || (!isLoading && agent === undefined)) {
    return (
      <section className={styles.view}>
        <div className={styles.head}>
          <BackButton onBack={back} />
        </div>
        <div className={styles.state}>{messageOf(error)}</div>
      </section>
    );
  }

  if (values === null || agent === undefined) {
    return (
      <section className={styles.view}>
        <div className={styles.head}>
          <BackButton onBack={back} />
        </div>
        <div className={styles.state}>
          <span className={styles.loading}>
            <Spinner />
            Loading agent…
          </span>
        </div>
      </section>
    );
  }

  const set = <K extends keyof Draft>(field: K, value: Draft[K]): void => {
    setDraft((current) =>
      current === null ? current : { ...current, values: { ...current.values, [field]: value } },
    );
  };

  const pending = changed.length;
  const saving = agentSaving.isLoading;

  const blankName = changed.includes('name') && values.name.trim() === '';

  const onSave = async (): Promise<void> => {
    const agentBody: AgentUpdate = {
      // Only what changed. The server treats a field equal to its current value
      // as no change anyway, but sending three fields to alter one would put the
      // other two in the audit log's blast radius for no reason.
      ...Object.fromEntries(changed.map((field) => [field, values[field]])),
      expectedSirmaUpdatedAt: agent.sirmaUpdatedAt,
    };

    if (changed.length === 0) return;

    try {
      const updated = await saveAgent({ id: agentId, body: agentBody }).unwrap();
      // Re-seeded from what the server returned rather than cleared: the new
      // record carries the platform's new timestamp, which the next save needs,
      // and clearing would flash the loading state over a page the operator is
      // still looking at.
      setDraft({ agentId, values: draftOf(updated) });
      dispatch(
        pushToast(
          'success',
          `Saved: ${readAsList(changed.map((field) => fieldLabels[field] ?? field))}.`,
        ),
      );
    } catch (failure) {
      // A conflict is kept as the operator's writing — discarding it to show
      // them somebody else's version would be the worse of the two failures.
      dispatch(pushToast('error', conflictOf(failure) ?? messageOf(failure)));
    }
  };

  return (
    <section className={styles.view}>
      <div className={styles.head}>
        <BackButton onBack={back} />

        <div className={styles.headRight}>
          {pending > 0 && (
            <span className={styles.unsaved}>
              <span className={styles.pip} aria-hidden="true" />
              {pending} unsaved change{pending === 1 ? '' : 's'}
            </span>
          )}

          <button
            type="button"
            className={styles.save}
            disabled={pending === 0 || blankName || saving}
            title={
              blankName
                ? 'A name cannot be empty'
                : pending === 0
                  ? 'Nothing has changed'
                  : undefined
            }
            onClick={() => void onSave()}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Only on the agent that speaks. The analyzer has a name too, and nobody
          ever hears or reads it, so there is nothing to offer. */}
      {(editable.has('name') || editable.has('dateContext')) && (
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h3>On the call</h3>
            <span className={styles.sub}>Who callers talk to</span>
          </div>

          <div className={styles.body}>
            {editable.has('name') && (
              <Field
                label="Agent name"
                hint="The name the agent introduces itself with on the call. Changing it changes what callers hear."
                value={values.name}
                max={AGENT_NAME_MAX}
              >
                <input
                  className={styles.input}
                  value={values.name}
                  maxLength={AGENT_NAME_MAX}
                  aria-label="Agent name"
                  onChange={(event) => set('name', event.target.value)}
                />
              </Field>
            )}

            {editable.has('dateContext') && (
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={values.dateContext}
                  onChange={(event) => set('dateContext', event.target.checked)}
                />
                <span>
                  <span className={styles.toggleTitle}>
                    Tell the agent today&rsquo;s date and time
                  </span>
                  <span className={styles.toggleHint}>
                    So it can answer &ldquo;what day is it&rdquo; and talk about tomorrow or next
                    week without guessing.
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      {agent.lines.map((line) => (
        <LineGreetingPanel key={line.id} lineId={line.id} />
      ))}

      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h3>Agent instructions</h3>
          <span className={styles.sub}>
            The system prompt that defines this agent&rsquo;s behaviour
          </span>
        </div>

        <div className={styles.body}>
          <div className={styles.editor}>
            <div className={styles.gutter} ref={gutterRef} aria-hidden="true">
              {Array.from({ length: lineCount }, (_, index) => (
                <div key={index}>{index + 1}</div>
              ))}
            </div>
            <textarea
              ref={editorRef}
              className={styles.area}
              value={values.instructions}
              spellCheck={false}
              maxLength={AGENT_INSTRUCTIONS_MAX}
              aria-label="Agent instructions"
              onScroll={() => {
                if (gutterRef.current !== null && editorRef.current !== null) {
                  gutterRef.current.scrollTop = editorRef.current.scrollTop;
                }
              }}
              onChange={(event) => set('instructions', event.target.value)}
            />
          </div>

          <div className={styles.editorFoot}>
            <span>{values.instructions.length.toLocaleString()} characters</span>
            <span>{lineCount.toLocaleString()} lines</span>

            {/* Bottom right. The platform's timestamp for the agent record, which
                is the only one there is — it moves for any field, so this is
                worded as the agent changing rather than the instructions.

                `lastUpdatedBy` is an address or nothing: Sirma reports an API-key
                id for anything edited over the API, so the server resolves our
                own changes back to the person who made them and drops an id it
                cannot resolve rather than printing it. */}
            <span className={styles.byline}>
              {agent.sirmaUpdatedAt === null
                ? 'Never updated'
                : `Agent last updated${
                    agent.lastUpdatedBy === null ? '' : ` by ${agent.lastUpdatedBy}`
                  } · ${when(agent.sirmaUpdatedAt)}`}
            </span>
          </div>
        </div>
      </div>

      <AgentHistory agentId={agentId} />
    </section>
  );
}

const fieldLabels: Record<string, string> = {
  name: 'agent name',
  instructions: 'instructions',
  dateContext: 'date setting',
};

function BackButton({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <button type="button" className={styles.back} onClick={onBack}>
      <IconChevronLeft width="16" height="16" />
      Back to agents
    </button>
  );
}
